import { TokenManager } from "@components/token-manager/TokenManager";
import React, { useRef, useState } from "react";
import styled from "@emotion/styled";
import { Callout, DirectionalHint } from "@fluentui/react";
import { classNames } from "maven-lib/dist/utils/misc";
import { CrIcon } from "@maven-surface/components/base/CrIcon";
import { IconName } from "@maven-surface/components/interface/Icon";

const ICON_SIZE = 50;

interface EcoaContentTitleBarButtonProps {
  icon: IconName;
  onClick?: () => void;
}

interface EcoaContentProps {
  children: React.ReactNode;
  className?: string;
  welcomeIcon?: IconName;
  verticalAlign?: "CENTER";
  barTitle?: string;
  leftBarButton?: EcoaContentTitleBarButtonProps;
  rightBarButton?: EcoaContentTitleBarButtonProps;
  showAuthTokenManager?: boolean;
  fontSize?: "SMALL" | "MEDIUM" | "LARGE";
  showController?: boolean;
}

interface StyledContentprops {
  verticalAlign?: "CENTER";
}

interface StyledContentWrapperProps {
  showController?: boolean;
}

const StyledContent = styled.div<StyledContentprops>`
  width: 100%;
  min-height: inherit;

  ${({ verticalAlign }) =>
    verticalAlign === "CENTER" &&
    `
      display: flex;
      flex-direction: column;
      justify-content: center;
  `}
`;

const StyledContentWrapper = styled.div<StyledContentWrapperProps>`
  background: #faf7f5;
  padding: 1rem 1rem ${({ showController }) => (showController ? "160px" : "72px")} 1rem;

  .app-hybrid-footer {
    margin-left: -1rem;
  }

  > .fx-guard {
    .fx-guard-loading {
      &.--silent {
        display: block !important;
      }
    }
  }
`;

const StyledContentWelcomeIcon = styled.div`
  width: 100%;
  text-align: center;

  i {
    width: ${ICON_SIZE};
    height: ${ICON_SIZE};
    margin-bottom: 2rem;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 0 1.5rem rgba(0, 0, 0, 0.05);
    font-size: 73px;
    line-height: ${ICON_SIZE};
  }
`;

const StyledBarTitle = styled.span`
  white-space: break-word;
  overflow: hidden;
  text-overflow: ellipsis;

  text-align: center;
  text-decoration: underline;

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const StyledTooltip = styled(Callout)`
  max-width: 300px;
  padding: 1rem;
  background-color: white;

  .ms-Callout-main {
    overflow: visible;
  }
`;

export function EcoaContent(props: EcoaContentProps) {
  const [tooltipVisibility, setTooltipVisibility] = useState(false);
  const tooltipRef = useRef(null);

  const { children, className, verticalAlign, welcomeIcon, barTitle, showAuthTokenManager, fontSize = "SMALL", showController } = props;

  return (
    <>
      {!!barTitle && (
        <strong
          className={classNames("app-hybrid-content-title-bar", {
            "font--small": fontSize === "SMALL",
            "font--medium": fontSize === "MEDIUM",
            "font--large": fontSize === "LARGE",
          })}
        >
          <StyledBarTitle
            ref={tooltipRef}
            onMouseMove={() => setTooltipVisibility(true)}
            onMouseLeave={() => setTimeout(() => setTooltipVisibility(false), 50)}
          >
            <span>{barTitle}</span>
            {tooltipVisibility && (
              <StyledTooltip target={tooltipRef?.current} doNotLayer={false} directionalHint={DirectionalHint.bottomCenter}>
                <span>{barTitle}</span>
              </StyledTooltip>
            )}
          </StyledBarTitle>
          {showAuthTokenManager && (
            <div className={classNames("app-hybrid-content-title-timer")}>
              <TokenManager />
            </div>
          )}
        </strong>
      )}
      <StyledContent className={classNames("app-hybrid-content", className)} verticalAlign={verticalAlign}>
        <StyledContentWrapper className="app-hybrid-content-wrapper" showController={showController}>
          {!!welcomeIcon && (
            <StyledContentWelcomeIcon>
              <CrIcon icon={welcomeIcon} />
            </StyledContentWelcomeIcon>
          )}
          {children}
        </StyledContentWrapper>
      </StyledContent>
    </>
  );
}
